import { motion } from "framer-motion";
import { Zap, CalendarClock, Truck, Forklift, ArrowUpRight } from "lucide-react";

const CARGO_IMG =
  "https://images.unsplash.com/photo-1553413077-190dd305871c?q=80&w=1600&auto=format&fit=crop";

const SERVICES = [
  {
    id: "same_day",
    icon: Zap,
    index: "S.01",
    title: "Same-Day Emergency Delivery",
    desc: "Dedicated point-to-point dispatch across Greater Phoenix Metro when the cargo can't wait until tomorrow.",
    featured: true,
  },
  {
    id: "scheduled",
    icon: CalendarClock,
    index: "S.02",
    title: "Scheduled Route Hauling",
    desc: "Predictable recurring shipments for B2B partners, suppliers, and distributors.",
    featured: false,
  },
  {
    id: "dock_to_door",
    icon: Truck,
    index: "S.03",
    title: "Dock-to-Door Service",
    desc: "Direct transit from industrial docks straight to commercial front doors.",
    featured: false,
  },
  {
    id: "liftgate_specialty",
    icon: Forklift,
    index: "S.04",
    title: "Hydraulic Lift-Gate Delivery",
    desc: "Ground-level unloading for sites without a dock or forklift on site.",
    featured: false,
  },
];

const cardMotion = (i) => ({
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
});

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative py-28 sm:py-36 bg-[#281D17]">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <motion.div {...cardMotion(0)} className="mb-16 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#C85A32] mb-5 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C85A32]" />
            03 // Freight Services
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F7F2EB] leading-tight">
            Four ways we move <span className="italic text-[#E89D4B]">the Valley.</span>
          </h2>
        </div>
        <a
          href="#quote"
          data-testid="services-quote-link"
          className="group flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#E89D4B] hover:text-[#F7F2EB] transition-colors duration-300"
        >
          Price a shipment
          <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              {...cardMotion(i + 1)}
              className={`group relative overflow-hidden rounded-xl border border-[#C85A32]/25 bg-[#2E211A] p-8 sm:p-10 transition-[border-color,transform] duration-500 hover:border-[#E89D4B]/70 hover:-translate-y-1 ${
                s.featured ? "md:col-span-4 min-h-[320px] flex flex-col justify-end" : "md:col-span-2"
              }`}
            >
              {s.featured && (
                <>
                  <img
                    src={CARGO_IMG}
                    alt="Palletized freight cargo ready for same-day dispatch"
                    className="absolute inset-0 w-full h-full object-cover opacity-35 transition-[opacity,transform] duration-700 group-hover:opacity-50 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1E1511] via-[#1E1511]/60 to-transparent" />
                </>
              )}
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <span className="w-11 h-11 rounded-full border border-[#C85A32]/40 flex items-center justify-center text-[#E89D4B] transition-colors duration-500 group-hover:bg-[#C85A32] group-hover:text-[#F7F2EB] group-hover:border-[#C85A32]">
                    <Icon size={19} strokeWidth={1.75} />
                  </span>
                  <span className="font-mono text-[11px] tracking-[0.25em] text-[#C85A32]">{s.index}</span>
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#F7F2EB] mb-3">{s.title}</h3>
                <p className="text-sm sm:text-base text-[#E6DACB]/80 leading-relaxed">{s.desc}</p>
              </div>
            </motion.article>
          );
        })}
      </div>
    </div>
  </section>
);
